import { fetchCartJson, syncHeaderCartCount } from '@theme/cart-visible-count';

/**
 * <product-card>
 *
 * Custom Element wrapping the product card. Handles:
 *  - Color swatch click → variant change (image, short description, price, URL)
 *  - Loading state during variant transition (image 40%, info gray overlay, ATC disabled)
 *  - ATC button click (ajax add-to-cart when the variant is fully determined;
 *    navigate to PDP only when the product has a size option to choose)
 *  - Optional persistent added state after a successful add when the card has
 *    `data-show-added-confirmation` (locks ATC while the product is in the cart)
 *  - Text overflow detection for single-line title/description fades
 *
 * Pairs with: <pc-image-slider> for the image carousel.
 *
 * Loaded ONLY via snippets/product-card.liquid (per-section pattern).
 *
 * Markup contract: see snippets/product-card.liquid.
 *   Required data-* on swatch button:
 *     data-action="select-variant"
 *     data-variant-id, data-variant-url, data-variant-available,
 *     data-variant-price (cents), data-variant-compare-at-price (cents),
 *     data-variant-image, data-variant-short-desc
 *   Required data-action="atc" on ATC buttons.
 *   Required: <pc-image-slider> for slider control.
 */

import { Delegate } from '@theme/delegate';

const LOADING_FADE_MS = 250;
const cartRequestsByUpdate = new WeakMap();

function fetchCartForUpdate(event) {
  const pendingRequest = cartRequestsByUpdate.get(event);
  if (pendingRequest) return pendingRequest;

  const request = fetchCartJson();
  cartRequestsByUpdate.set(event, request);
  return request;
}

class ProductCard extends HTMLElement {
  #delegate = null;
  #link = null;
  #slider = null;
  #firstSlideImage = null;
  #shortDescEl = null;
  #priceRegular = null;
  #priceCompare = null;
  #priceCompareValue = null;
  #atcButtons = [];
  #overflowObserver = null;
  #overflowElements = [];
  #cartSyncGeneration = 0;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    // The cart-drawer upsell card is the same component with its own BEM prefix, so both
    // naming sets are matched here - otherwise a swatch click there updated the price and
    // description while the image and link stayed on the previous colour.
    this.#link = this.querySelector('.product-card__link, .product-card-cart-upsell__link');
    this.#slider = this.querySelector('pc-image-slider');
    // img., because the placeholder SVG carries the same class on products with no media -
    // setting .src on an <svg> silently does nothing and the card keeps the old colour.
    this.#firstSlideImage = this.querySelector(
      '.product-card__slide .product-card__image, .product-card__image--single, img.product-card-cart-upsell__image'
    );
    this.#shortDescEl = this.querySelector('[data-short-description]');
    this.#priceRegular =
      this.querySelector('.priceItem--regular') ?? this.querySelector('.cart-item__price > span.u-p4');
    this.#priceCompare =
      this.querySelector('.priceCompare') ?? this.querySelector('.cart-item__price-before');
    this.#priceCompareValue = this.querySelector('.priceItem--compare');
    this.#atcButtons = Array.from(this.querySelectorAll('[data-action="atc"]'));
    this.#overflowElements = Array.from(
      this.querySelectorAll(
        '.product-card__title, .product-card__short-desc, .product-card-cart-upsell__info-title, .product-card-cart-upsell__short-desc'
      )
    );

    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-action="select-variant"]', this.#onSwatchClick.bind(this));
    this.#delegate.on('click', '[data-action="atc"]', this.#onATCClick.bind(this));

    this.#overflowObserver = new ResizeObserver(this.#updateTextOverflow);
    this.#overflowElements.forEach((element) => this.#overflowObserver.observe(element));
    this.#updateTextOverflow();

    if (this.hasAttribute('data-show-added-confirmation')) {
      document.addEventListener('cart:updated', this.#onCartUpdated);
    }
  }

  disconnectedCallback() {
    this.#delegate?.off();
    this.#delegate = null;
    this.#overflowObserver?.disconnect();
    this.#overflowObserver = null;
    this.#overflowElements = [];
    document.removeEventListener('cart:updated', this.#onCartUpdated);
    this.#cartSyncGeneration += 1;
    this.initialized = false;
  }

  #updateTextOverflow = () => {
    this.#overflowElements.forEach((element) => {
      const isOverflowing =
        element.clientWidth > 0 && element.scrollWidth > element.clientWidth + 1;
      element.toggleAttribute('data-overflowing', isOverflowing);
    });
  };

  #setLoading(on) {
    if (on) this.setAttribute('data-loading', 'true');
    else this.removeAttribute('data-loading');
  }

  // Persistent confirmation for opted-in cards. ATC stays locked while the product
  // is in the cart; removing it from the cart restores the card's ATC state.
  #setAdded(on) {
    if (on) {
      this.setAttribute('data-added', 'true');
    } else {
      this.removeAttribute('data-added');
    }

    if (!this.hasAttribute('data-show-added-confirmation')) return;

    this.#atcButtons.forEach((button) => {
      if (on || this.classList.contains('product-card--out-of-stock')) {
        button.setAttribute('disabled', '');
      } else {
        button.removeAttribute('disabled');
      }

      if (button.hasAttribute('aria-label')) {
        const label = on ? button.dataset.inCartLabel : button.dataset.atcLabel;
        if (label) button.setAttribute('aria-label', label);
      }
    });
  }

  #onCartUpdated = (event) => {
    this.#syncAddedFromCart(event);
  };

  async #syncAddedFromCart(event) {
    const productId = this.dataset.productId;
    if (!productId) return;

    const generation = ++this.#cartSyncGeneration;
    const productIds = event.detail?.product_ids;

    if (Array.isArray(productIds)) {
      const isInCart = productIds.some((id) => String(id) === String(productId));
      this.#setAdded(isInCart);
      return;
    }

    try {
      const cart = await fetchCartForUpdate(event);
      if (generation !== this.#cartSyncGeneration || !this.isConnected) return;

      const isInCart = (cart.items || []).some(
        (item) => String(item.product_id) === String(productId)
      );
      this.#setAdded(isInCart);
    } catch {
      // Keep the current UI state if cart synchronization fails.
    }
  }

  async #onSwatchClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const btn = event.target.closest('[data-action="select-variant"]');
    if (!btn) return;

    if (btn.classList.contains('is-selected')) return;
    if (this.hasAttribute('data-loading')) return;

    this.#setLoading(true);

    await new Promise((resolve) => setTimeout(resolve, LOADING_FADE_MS));

    this.#updateSwatchSelected(btn);
    this.#updateImage(btn.dataset.variantImage);
    this.#updateShortDesc(btn.dataset.variantShortDesc);
    this.#updatePrice(btn.dataset.variantPrice, btn.dataset.variantCompareAtPrice);
    this.#updateLink(btn.dataset.variantUrl);
    this.#updateAvailability(btn.dataset.variantAvailable === 'true');
    this.#resetSlider();

    this.#setLoading(false);
  }

  #updateSwatchSelected(btn) {
    this.querySelectorAll('.product-card__swatch').forEach((s) => {
      s.classList.remove('is-selected');
      s.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');
  }

  #updateImage(newSrc) {
    if (!newSrc || !this.#firstSlideImage) return;
    this.#firstSlideImage.src = newSrc;
    this.#firstSlideImage.removeAttribute('srcset');
  }

  #updateShortDesc(text) {
    if (!this.#shortDescEl) return;
    this.#shortDescEl.textContent = text || '';
    this.#shortDescEl.style.display = text ? '' : 'none';
    this.#updateTextOverflow();
  }

  #updatePrice(priceCents, compareAtCents) {
    const fmt = (cents) => {
      if (typeof Shopify?.formatMoney !== 'function' || !window.theme?.moneyFormat) return null;
      // Match Liquid `money_without_trailing_zeros` (strip .00 / ,00).
      return Shopify.formatMoney(parseInt(cents, 10), window.theme.moneyFormat).replace(
        /([.,])00(\D|$)/,
        '$2',
      );
    };

    const regularFormatted = fmt(priceCents);
    if (this.#priceRegular && regularFormatted) this.#priceRegular.textContent = regularFormatted;

    if (!this.#priceCompare) return;
    const compareAt = parseInt(compareAtCents, 10);
    if (compareAt > 0 && compareAt > parseInt(priceCents, 10)) {
      this.#priceCompare.removeAttribute('hidden');
      this.#priceCompare.style.display = '';
      const compareFormatted = fmt(compareAt);
      if (this.#priceCompareValue && compareFormatted) {
        this.#priceCompareValue.textContent = compareFormatted;
      } else if (compareFormatted) {
        this.#setCartDrawerComparePrice(compareFormatted);
      }
    } else {
      this.#priceCompare.style.display = 'none';
      this.#priceCompare.setAttribute('hidden', '');
    }
  }

  #setCartDrawerComparePrice(formatted) {
    if (!this.#priceCompare?.classList.contains('cart-item__price-before')) return;

    const hidden = this.#priceCompare.querySelector('.VisuallyHidden');
    this.#priceCompare.replaceChildren();
    if (hidden) this.#priceCompare.appendChild(hidden);
    this.#priceCompare.append(document.createTextNode(formatted));
  }

  #updateLink(newUrl) {
    if (!newUrl || !this.#link) return;
    this.#link.href = newUrl;
  }

  #updateAvailability(isAvailable) {
    this.classList.toggle('product-card--out-of-stock', !isAvailable);
    this.#atcButtons.forEach((b) => {
      if (isAvailable && !this.hasAttribute('data-added')) b.removeAttribute('disabled');
      else b.setAttribute('disabled', '');
    });
  }

  #resetSlider() {
    if (this.#slider && typeof this.#slider.goTo === 'function') {
      this.#slider.goTo(0);
    }
  }

  async #onATCClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasAttribute('data-added')) return;

    const productUrl = this.dataset.productUrl;
    const selectedSwatch = this.querySelector('.product-card__swatch.is-selected');

    // Whether to open the PDP depends on the SELECTED colour, not the product:
    // a variant-mode swatch carries how many variants its colour has (one = fully
    // determined -> add; more = there is a size to choose -> PDP). Connected swatches
    // (links) have no count, so fall back to the product-level has-size flag.
    const swatchCount = selectedSwatch ? parseInt(selectedSwatch.dataset.variantCount, 10) : NaN;
    const goToPdp = Number.isNaN(swatchCount)
      ? this.dataset.hasSize === 'true'
      : swatchCount > 1;

    if (goToPdp) {
      const target = selectedSwatch?.dataset?.variantUrl || selectedSwatch?.getAttribute('href') || productUrl;
      if (target) window.location.href = target;
      return;
    }

    const variantId =
      selectedSwatch?.dataset?.variantId ||
      this.dataset.variantId ||
      this.dataset.defaultVariantId;
    if (!variantId) {
      if (productUrl) window.location.href = productUrl;
      return;
    }

    // Never POST an unavailable variant — Shopify returns 422 ("sold out"). The
    // out-of-stock class is kept in sync with the current colour (render + swatch
    // change), so it is the availability source of truth. Send the shopper to the PDP
    // (stock status shows there; they can pick an in-stock variant) instead of adding.
    if (this.classList.contains('product-card--out-of-stock')) {
      const target = selectedSwatch?.dataset?.variantUrl || selectedSwatch?.getAttribute('href') || productUrl;
      if (target) window.location.href = target;
      return;
    }

    const keepLoadingUntilCartScroll = this.#isMainCartRecommendation();

    this.#setLoading(true);
    try {
      const response = await fetch(`${window.routes.localeRootUrl}/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`ATC failed: ${response.status}`);

      if (!keepLoadingUntilCartScroll && this.hasAttribute('data-show-added-confirmation')) {
        // PDP regular-upsell cards remain in the success state after adding. Further
        // quantity changes are made from the cart rather than from the upsell card.
        this.#cartSyncGeneration += 1;
        this.#setAdded(true);
      }

      this.dispatchEvent(
        new CustomEvent('product:added', {
          bubbles: true,
          detail: {
            variant: parseInt(variantId, 10),
            quantity: 1,
          },
        })
      );
      document.dispatchEvent(
        new CustomEvent('product:added', {
          detail: {
            variant: parseInt(variantId, 10),
            quantity: 1,
          },
        })
      );
      await syncHeaderCartCount();
    } catch {
      // Silent fail; merchant can wire a cart:error listener if needed.
      this.#setLoading(false);
    } finally {
      if (!keepLoadingUntilCartScroll) {
        this.#setLoading(false);
      }
    }
  }

  #isMainCartRecommendation() {
    return !!this.closest('main-cart .main-cart__recommendations');
  }

  finishMainCartRecommendationFlow() {
    this.#setAdded(false);
    this.#setLoading(false);
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
